import { CellContext, createColumnHelper } from '@tanstack/react-table';

import { Guideline, SliderCheckbox, Tooltip } from '@components/common';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell/AdaptiveTableCell';

import { TExtendedTableColumns } from '@interfaces/components/table';
import { IRUStatusDashboard } from '@interfaces/ruStatusDashboard/ruStatusDashboard';

import { ELockedUnlocked } from '@enums/common';

import styles from './RUStatusDashboard.module.scss';

interface IColumns {
	setSelectedRUStatus: (selectedConsistencyCheckRan: IRUStatusDashboard) => void;
	openCarrierStateTableModal: () => void;
	toggleTreeModal: () => void;
	toggleAvailability: (id: string, isEnabled: boolean) => void;
	toggleEnergySavingMode: (id: string, isEnabled: boolean) => void;
	isViewerOnly: boolean;
}

const columnHelper = createColumnHelper<IRUStatusDashboard>();

export const getColumns = ({
	openCarrierStateTableModal,
	setSelectedRUStatus,
	toggleTreeModal,
	toggleAvailability,
	toggleEnergySavingMode,
	isViewerOnly,
}: IColumns): TExtendedTableColumns<IRUStatusDashboard>[] => [
	columnHelper.accessor('ruName', {
		header: 'RU Name',
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUStatusDashboard, any>) => {
			const {
				softwareRev,
				serialNum,
				modelName,
				ruName,
			} = original || {};

			return (
				<div className={styles.cellWithGuideline}>
					<div>{ruName}</div>
					<Guideline
						guidelineText={
							<div className={styles.guidelineWrapper}>
								<div>Software revision: {softwareRev}</div>
								<div>Serial number: {serialNum}</div>
								<div>Model name: {modelName}</div>
							</div>
						}
					/>
				</div>
			);
		},
	}),
	columnHelper.accessor('netconfName', {
		header: 'Netconf name',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const value = getValue();

			return <AdaptiveTableCell text={value}/>;
		},
	}),
	columnHelper.accessor('administrativeState', {
		header: () => <>Administrative<br/>State</>,
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUStatusDashboard, any>) => {
			const {
				id,
				administrativeState,
				isEnergySavingMode,
			} = original || {};

			const isUnlocked = administrativeState.name === ELockedUnlocked.unlocked;

			const handleToggle = () => {
				toggleAvailability(id, isUnlocked);
			};

			return (
					<div className={styles.sliderCell}>
						<SliderCheckbox
							checked={isUnlocked}
							disabled={isViewerOnly || isEnergySavingMode}
							id='monitoringStatus'
							onChange={handleToggle}
						/>
					</div>
			);
		},
	}),
	columnHelper.accessor('operationalState', {
		header: () => <>Operational<br/>State</>,
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('usageState', {
		header: 'Usage State',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('powerState', {
		header: 'Power State',
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('availabilityState', {
		header: () => <>Availability<br/>State</>,
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const { title, color } = getValue() || {};

			return (
				<AdaptiveTableCell
					color={color}
					text={title || 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('synchronisationState', {
		header: () => <>Synchronisation<br/>State</>,
		enableSorting: false,
		cell: ({ getValue }: CellContext<IRUStatusDashboard, any>) => {
			const { title } = getValue() || {};

			return <AdaptiveTableCell text={title || 'N/A'} />;
		},
	}),
	columnHelper.accessor('ietfInterfaces', {
		header: () => <>O-RU interfaces<br/>state and statistics</>,
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUStatusDashboard, any>) => {
			const { isInterfacesAvailable } = original || {};

			const onClick = () => {
				if (!isInterfacesAvailable) {
					return;
				}

				toggleTreeModal();
				setSelectedRUStatus(original);
			};

			return (
				<AdaptiveTableCell
					className={isInterfacesAvailable ? styles.actionCell : undefined}
					onClick={onClick}
					text={isInterfacesAvailable ? 'View' : 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('carriers', {
		header: () => <>TX/RX Carrier<br/>state</>,
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUStatusDashboard, any>) => {
			const { isTxRxCarriersAvailable } = original || {};

			const onClick = () => {
				if (!isTxRxCarriersAvailable) {
					return;
				}

				openCarrierStateTableModal();
				setSelectedRUStatus(original);
			};

			return (
				<AdaptiveTableCell
					className={isTxRxCarriersAvailable ? styles.actionCell : undefined}
					onClick={onClick}
					text={isTxRxCarriersAvailable ? 'View' : 'N/A'}
				/>
			);
		},
	}),
	columnHelper.accessor('isEnergySavingMode', {
		header: () => <>Energy Saving<br/>Mode</>,
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IRUStatusDashboard, any>) => {
			const {
				isEnergySavingMode: isEnabled,
				ruName,
				administrativeState,
			} = original || {};

			const isEnergySavingModeDisabled = administrativeState.name === ELockedUnlocked.locked;

			const handleToggle = () => {
				if (ruName) {
					toggleEnergySavingMode(original.id, !isEnabled);
				}
			};

			return (
				<div className={styles.actionCell}>
					{ruName
						? (
							<Tooltip content={isEnabled ? 'Disable Energy Saving Mode' : 'Enable Energy Saving Mode'}>
								<div>
									<SliderCheckbox
										checked={isEnabled}
										disabled={isViewerOnly || isEnergySavingModeDisabled}
										id='energySavingMode'
										onChange={handleToggle}
									/>
								</div>
							</Tooltip>

						)
						: (<AdaptiveTableCell text='N/A'/>)
					}
				</div>
			);
		},
	}),
];
